// ===================================
// AUTH CONTEXT - src/contexts/AuthContext.tsx
// ===================================
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import CryptoJS from "crypto-js";
import api from "../services/api";
import { User } from "../types";
import Header from "../components/Header";

// const aesSalt = process.env.REACT_APP_AES_SALT as string;
// const singleLoginBEUrl = process.env.REACT_APP_SINGLELOGIN_BE_REDIRECT_URL;
// const moduleLink = process.env.REACT_APP_FE_MODULE_URL;

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  updateProfile: (updates: Partial<User>) => void;
  verifyUserApi: (token: string) => Promise<User>;
  autoLogin: ({ user, verifiedToken }: any) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem("accessToken");
      if (token) {
        try {
          const response = await api.get("/auth/me");
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem("accessToken");
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (username: string, password: string): Promise<void> => {
    try {
      // const response = await api.post("/auth/login", { username, password });
      // const [idToken, businessesList] = await loginWithEmailPassword(
      //   username,
      //   password
      // );
      // localStorage.setItem("accessToken", response.data.access_token);
      // setUser(response.data.user);
    } catch (error) {
      throw error;
    }
  };

  const verifyUserApi = async (token: string): Promise<User> => {
    try {
      const response = await api.post("/auth/login-v2", { token });
      const verifiedToken = response.data.access_token;

      localStorage.setItem("accessToken", verifiedToken);
      localStorage.setItem("token", verifiedToken);
      setUser(response.data.user);
      return response.data;
    } catch (error) {
      throw new Error("User verification failed");
    }
  };

  const autoLogin = async ({ verifiedToken, user }: any) => {
    localStorage.setItem("accessToken", verifiedToken);
    localStorage.setItem("token", verifiedToken);
    setUser(user);
  };

  const logout = () => {
    localStorage.removeItem("accessToken");
    setUser(null);

    // Call logout endpoint (fire and forget)
    api.post("/auth/logout").catch(() => {});
  };

  const updateProfile = (updates: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...updates });
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateProfile,
    verifyUserApi,
    autoLogin,
  };

  // const loginWithEmailPassword = async (email: string, password: string) => {
  //   try {
  //     // Encrypt the password before sending
  //     const encryptedPassword = encryptPassword(password);

  //     const response = await fetch(
  //       `${singleLoginBEUrl}/users/login-with-email-password`,
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //         },
  //         body: JSON.stringify({
  //           email: email,
  //           password: encryptedPassword,
  //           moduleUrl: moduleLink,
  //         }),
  //       }
  //     );

  //     // if (!response.ok) {
  //     //   const errorData = await response.json().catch(() => ({}));
  //     //   throw new Error(errorData?.message || `Authentication failed with status: ${response.status}`);
  //     // }

  //     const data = await response.json();

  //     // Check if businesses are returned in the response
  //     if (data.businessesList) {
  //       return [data.idToken, data.businessesList];
  //     } else {
  //       throw new Error("No businesses received from server");
  //     }
  //   } catch (error) {
  //     throw error;
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  // const encryptPassword = (plaintext: string) => {
  //   const password = CryptoJS.AES.encrypt(plaintext, aesSalt).toString();
  //   return password;
  // };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#020617]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8b3dff] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
